import React from 'react';
import Grid from '@mui/material/Grid';
import { memberProps, useAuth } from '../../context';
import { useAlert, useFetch } from '../../hooks';
import {
    Form,
    Field,
    InputEventProps,
    FormType,
    ActionButton,
    Alert
} from '..';

interface ChangeDetailsProps {
    memberDetails: memberProps | null;
    closeHandler: () => void;
}

export interface EditMemberValuesProps {
    Name?: string;
    Surname?: string;
    Email?: string;
    Phone?: number | string;
    Contact?: number | string;
    Address?: string;
}

export const ChangeDetails: React.FC<ChangeDetailsProps> = React.memo(
    ({ memberDetails, closeHandler }) => {
        const { session } = useAuth();
        const [fetchHandler, loading] = useFetch(
            '/dbEmployeeEditDetails',
            'PUT'
        );
        const { alert, dispatchAlert, closeAlert } = useAlert();
        const editMemberValuesInitial: EditMemberValuesProps =
            React.useMemo(() => {
                return {
                    Name: memberDetails?.Name,
                    Surname: memberDetails?.Surname,
                    Email: memberDetails?.Email,
                    Phone: memberDetails?.Phone,
                    Contact: memberDetails?.Contact,
                    Address: memberDetails?.Address
                };
            }, [
                memberDetails?.Address,
                memberDetails?.Contact,
                memberDetails?.Email,
                memberDetails?.Name,
                memberDetails?.Phone,
                memberDetails?.Surname
            ]);
        const [editMemberValues, setEditMemberValues] =
            React.useState<EditMemberValuesProps>(editMemberValuesInitial);

        const onChangeHandler = React.useCallback(
            (key: keyof EditMemberValuesProps) => (e: InputEventProps) =>
                setEditMemberValues(prevEditMemberValues => {
                    return {
                        ...prevEditMemberValues,
                        [key]: e.target.value
                    };
                }),
            []
        );

        const successHandler = React.useCallback(() => {
            closeHandler();
            dispatchAlert({
                type: 'UPDATE_ALERT',
                message: 'Success!',
                severity: 'success'
            });
        }, [closeHandler, dispatchAlert]);
        const errorHandler = React.useCallback(
            message => {
                dispatchAlert({
                    type: 'UPDATE_ALERT',
                    message: message,
                    severity: 'error'
                });
            },
            [dispatchAlert]
        );

        const editDetails = React.useCallback(
            async (e: FormType) => {
                e.preventDefault();
                const result = await fetchHandler({
                    data: {
                        member_guid: session.MemberGuid,
                        session_guid: session.SessionGuid,
                        data: editMemberValues
                    }
                });
                result.success ? successHandler() : errorHandler(result.msg);
                dispatchAlert({ type: 'OPEN' });
            },
            [
                fetchHandler,
                session.MemberGuid,
                session.SessionGuid,
                editMemberValues,
                successHandler,
                errorHandler,
                dispatchAlert
            ]
        );

        return (
            <Form
                sx={{ maxWidth: '40rem', width: '100%', margin: '1rem auto' }}
                onSubmit={editDetails}
            >
                <Alert {...alert} closeHandler={closeAlert} />
                <Grid item xs={12}>
                    <h1 style={{ fontSize: '2rem', marginBottom: '2rem' }}>
                        Change Details
                    </h1>
                </Grid>
                <Field name='Name' sm={6} onChange={onChangeHandler('Name')} />
                <Field
                    name='Surname'
                    sm={6}
                    onChange={onChangeHandler('Surname')}
                />
                <Field
                    name='Email'
                    sm={4}
                    onChange={onChangeHandler('Email')}
                />
                <Field
                    name='Phone'
                    sm={4}
                    onChange={onChangeHandler('Phone')}
                />
                <Field
                    name='Contact'
                    sm={4}
                    onChange={onChangeHandler('Contact')}
                />
                <Field name='Address' onChange={onChangeHandler('Address')} />
                <ActionButton
                    type='submit'
                    title='Submit'
                    sm={6}
                    variant='contained'
                    loading={loading}
                    grid
                />
                <ActionButton
                    type='button'
                    title='Submit'
                    variant='outlined'
                    sm={6}
                    onClick={closeHandler}
                    grid
                />
            </Form>
        );
    }
);
